import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Project } from '@/types/project';
import type { WebpageDraftStatus } from '@/types/project';
import type { Site } from '@/types/site';
import { nanoid } from 'nanoid';
import { normalizeLegacyPlatformDomain, normalizeLegacyPlatformUrl } from '@/lib/platformDomain';
import { buildPublishedPageUrl } from '@/lib/publishedPageUrl';
import type { GenerateWebpageRequestConfig } from '@/lib/webpageGeneration';

const normalizeStudioSite = (site: Site | null): Site | null => {
  if (!site) return null;
  return {
    ...site,
    domain: normalizeLegacyPlatformDomain(site.domain) || site.domain,
    originalDomain: normalizeLegacyPlatformDomain(site.originalDomain) || site.originalDomain,
    siteKey: normalizeLegacyPlatformDomain(site.siteKey) || site.siteKey,
  };
};

const normalizeStudioProject = (project: Project | null): Project | null => {
  if (!project) return null;

  return {
    ...project,
    projectPath: normalizeLegacyPlatformUrl(project.projectPath) || project.projectPath,
    sitePaths: project.sitePaths
      ? project.sitePaths.map((sitePath) => ({
          ...sitePath,
          domain: normalizeLegacyPlatformDomain(sitePath.domain) || sitePath.domain,
          originalDomain:
            normalizeLegacyPlatformDomain(sitePath.originalDomain) || sitePath.originalDomain,
        }))
      : null,
  };
};

/**
 * StudioMode - Studio 工作模式
 * 'expert' 为专家模式，'creator' 为创作模式，'' 为未选择模式。
 */
export type StudioMode = 'expert' | 'creator' | '';
export type BuildVariant = 'standard' | 'clone' | 'product';
export type AutoPublishSource = 'autopilot-clone';

export interface BuildTrackingContext {
  createMethod?: string;
  promptLength?: number;
  sectionCount?: number;
  hasWebsiteUrl?: boolean;
  templateMethod?: string | null;
}

export interface AutoPublishContext {
  source: AutoPublishSource;
  enabled: boolean;
  targetSiteId?: string | null;
}

/**
 * PreviewDevice - 预览设备类型
 * 'desktop' 桌面端, 'tablet' 平板端, 'mobile' 移动端。
 */
export type PreviewDevice = 'desktop' | 'tablet' | 'mobile';
export type MobileWorkspaceView = 'edit' | 'preview';

export type HistoryItemStatus = 'done' | 'pending' | 'review' | 'dismissed';

/**
 * HistoryItem - 构建或编辑历史记录项
 */
export interface HistoryItem {
  /** 记录唯一 ID */
  id: string;
  /** 记录标题 (如：“更新主页文案”) */
  title: string;
  /** 详细描述或改动日志 */
  detail: string;
  /** 当前状态：已完成或进行中 */
  status: HistoryItemStatus;
  /** 修改来源 (如：'AI', 'User') */
  source: string;
  /** 发生时间戳字符串 */
  at: string;
}

export type PreviewReviewView = 'before' | 'after';

export interface PreviewReviewState {
  active: boolean;
  draftId: number | string | null;
  version: number | null;
  view: PreviewReviewView;
  beforeHtml: string;
  afterHtml: string;
  title: string;
  note: string;
  sourceMessageId: string | null;
}

/**
 * StudioState - Studio 核心业务状态接口
 * 涵盖了当前正在编辑的项目详情、编辑器模式、预览设置以及构建相关的流转状态。
 */
interface StudioState {
  // --- 当前项目元数据 ---
  /** 当前编辑的项目 ID (String 形式) */
  currentId: string | null;
  /** 关联的项目主表 ID */
  currentProjectId: string | null;
  /** 关联的站点 ID */
  currentSiteId: string | null;
  /** 当前项目的源代码路径 */
  currentPath: string | null;
  /** 项目模板或类型 ID */
  currentProjectTypeId: string | null;
  /** 当前显示的预览项目名称 */
  currentName: string;
  /** 当前项目的完整对象数据 */
  currentProject: Project | null;
  /** 当前预览页面的 URL (带缓存击穿参数) */
  iframeUrl: string | null;
  /** 项目关联的 Logo 图片 URL */
  currentLogo: string | null;
  /** 当前编辑会话的唯一标识 (用于 WebSocket 或 API 追踪) */
  sessionId: string | null;

  // --- 编辑器配置 (部分支持持久化) ---
  /** 编辑器当前的模式 (专家/创作) */
  studioMode: StudioMode;
  /** 预览区域当前的模拟设备 */
  previewDevice: PreviewDevice;
  mobileWorkspaceView: MobileWorkspaceView;

  // --- 运行状态 ---
  /** 标志位：当前是否正在执行构建或发布流程 */
  isBuilding: boolean | null;
  /** 标志位：构建流程是否已完成（isDone） */
  isBuildDone: boolean;
  /** 待处理或正在执行的命令详情 */
  pendingCommand: string | null;
  pendingGenerateRequestConfig: GenerateWebpageRequestConfig | null;
  /** 当前构建入口类型 */
  buildVariant: BuildVariant;
  buildTrackingContext: BuildTrackingContext | null;
  /** 复刻模式来源 URL */
  cloneSourceUrl: string | null;
  productRequestConfig: GenerateWebpageRequestConfig | null;
  autoPublishContext: AutoPublishContext | null;

  // --- 历史记录 ---
  /** 当前项目的编辑/构建记录列表 */
  historyItems: HistoryItem[];
  /** 当前选中的历史记录详情（用于回滚或查看） */
  selectedHistoryItem: HistoryItem | null;

  // --- UI 交互状态 (不持久化) ---
  /** 历史侧边栏是否展开 */
  isHistoryOpen: boolean;
  /** 设置抽屉/弹窗是否打开 */
  isSettingsOpen: boolean;
  /** 历史记录主弹窗是否打开 */
  isHistoryModalOpen: boolean;
  /** 历史记录列表弹窗是否打开 */
  isHistoryListModalOpen: boolean;
  /** 发布确认弹窗是否打开 */
  isPublishModalOpen: boolean;
  /** 发布成功弹窗是否打开 */
  isPublishSuccessModalOpen: boolean;
  /** 预览 iframe 的 DOM 元素引用 */
  iframeElement: HTMLIFrameElement | null;
  /** AI 网页模式下实时生成的 HTML 片段缓存 */
  generatedHtml: string;
  previewHtmlOverride: string | null;
  creditBalance: number | null;
  creditBalanceLoading: boolean;
  /** 当前已提交到前端的草稿 ID */
  currentDraftId: number | string | null;
  /** 当前已提交到前端的草稿版本号 */
  currentDraftVersion: number | null;
  /** 当前已提交到前端的草稿状态 */
  currentDraftStatus: WebpageDraftStatus | null;
  /** 待确认草稿的预览比较状态 */
  previewReview: PreviewReviewState;
  /** 预览 iframe 是否正被专家/应用流程临时锁定 */
  isPreviewApplying: boolean;

  // --- Actions ---
  /**
   * 快速设置当前项目的基本标识
   * @param id - 项目 ID
   * @param path - 项目路径
   * @param name - 项目显示名称
   * @param siteId - 站点 ID
   */
  setCurrentProject: (
    id: string | null,
    path?: string | null,
    name?: string,
    siteId?: string | null,
  ) => void;
  /**
   * 完整同步项目数据，并自动映射其属性到状态机各字段
   * @param project - 后端返回的完整项目对象
   */
  setCurrentProjectData: (project: Project | null) => void;
  /**
   * 更新当前的会话 ID
   * @param id - 会话标识符
   */
  setSessionId: (id: string | null) => void;
  /**
   * 更新当前的站点 ID
   * @param id - 站点标识符
   */
  setSiteId: (id: string | null) => void;
  /**
   * 更新当前预览页面的 URL
   * @param url - 预览地址
   */
  setIframeUrl: (url: string | null) => void;
  /**
   * 设置 iframe 的 DOM 元素引用
   * @param el - iframe 元素
   */
  setIframeElement: (el: HTMLIFrameElement | null) => void;
  /**
   * 更新实时生成的 HTML 片段
   * @param html - HTML 字符串或更新函数
   */
  setGeneratedHtml: (html: string | undefined) => void;
  setPreviewHtmlOverride: (html: string | null) => void;
  setCreditBalanceState: (balance: number | null, loading?: boolean) => void;
  setCurrentDraftMeta: (
    draftId: number | string | null,
    version?: number | null,
    status?: WebpageDraftStatus | null,
  ) => void;
  startPreviewReview: (
    review: Omit<PreviewReviewState, 'active' | 'view'> & { view?: PreviewReviewView },
  ) => void;
  setPreviewReviewView: (view: PreviewReviewView) => void;
  clearPreviewReview: () => void;
  setPreviewApplying: (val: boolean) => void;
  /**
   * 切换 Studio 模式 (专家/创作)
   * @param mode - 目标模式
   */
  setStudioMode: (mode: StudioMode) => void;
  /**
   * 切换预览设备尺寸
   * @param device - 目标设备
   */
  setPreviewDevice: (device: PreviewDevice) => void;
  setMobileWorkspaceView: (view: MobileWorkspaceView) => void;
  /**
   * 更新构建加载状态
   * @param val - 是否正在构建
   */
  setIsBuilding: (val: boolean | null) => void;
  setIsBuildDone: (val: boolean) => void;
  setBuildVariant: (
    variant: BuildVariant,
    cloneSourceUrl?: string | null,
    productRequestConfig?: GenerateWebpageRequestConfig | null,
  ) => void;
  setBuildTrackingContext: (context: BuildTrackingContext | null) => void;
  setAutoPublishContext: (context: AutoPublishContext | null) => void;
  /**
   * 设置待处理的系统命令，通常由 AI 面板发出
   * @param command - 命令对象
   */
  setPendingCommand: (command: string | null) => void;
  setPendingGenerateRequestConfig: (config: GenerateWebpageRequestConfig | null) => void;
  /**
   * 批量更新历史记录列表
   * @param items - 历史记录数组
   */
  setHistoryItems: (items: HistoryItem[]) => void;
  /**
   * 追加单条历史记录
   * @param item - 记录项
   */
  addHistoryItem: (item: Partial<HistoryItem>) => void;
  /**
   * 选中某条具体的历史记录
   * @param item - 记录项
   */
  setSelectedHistoryItem: (item: HistoryItem | null) => void;

  /**
   * 清除当前项目的所有关联状态，通常在退出 Studio 或卸载 Layout 时调用
   */
  clearStudioState: () => void;

  /** 当前站点信息（域名绑定状态来源） */
  currentSiteInfo: Site | null;
  /** 更新当前站点信息 */
  setCurrentSiteInfo: (site: Site | null) => void;
  /** Google Ads Tag 是否已成功连接 */
  isGadsTagConnected: boolean;
  /** 设置 Google Ads Tag 连接状态 */
  setGadsTagConnected: (val: boolean) => void;

  // --- UI Actions ---
  /** 设置侧边栏历史面板是否显示 */
  setHistoryOpen: (val: boolean) => void;
  /** 切换历史面板显示状态 */
  toggleHistory: () => void;
  /** 设置设置面板是否显示 */
  setSettingsOpen: (val: boolean) => void;
  /** 当前选中的设置页签 */
  activeSettingsTab: string;
  /** 设置当前选中的页签 */
  setActiveSettingsTab: (tab: string) => void;
  /** 切换设置面板显示状态 */
  toggleSettings: () => void;
  /** 设置历史详情弹窗是否显示 */
  setHistoryModalOpen: (val: boolean) => void;
  /** 设置历史列表弹窗是否显示 */
  setHistoryListModalOpen: (val: boolean) => void;
  /** 设置发布确认弹窗是否显示 */
  setPublishModalOpen: (val: boolean) => void;
  /** 设置发布成功弹窗是否显示 */
  setPublishSuccessModalOpen: (val: boolean) => void;
  /** 最近一次发布的预览 URL */
  lastPublishedUrl: string | null;
  /** 设置最近一次发布的预览 URL */
  setLastPublishedUrl: (url: string | null) => void;
}

/**
 * useStudioStore - Studio 编辑器业务状态中心 (Zustand)
 * 使用 persist 中间件持久化存储核心配置（如模式、设备和当前项目），
 * 保证用户刷新页面后能立即回到之前的编辑上下文。
 */
export const useStudioStore = create<StudioState>()(
  persist(
    (set) => ({
      // --- 初始数据 ---
      currentId: null,
      currentProjectId: null,
      currentSiteId: null,
      currentPath: null,
      currentProjectTypeId: null,
      currentName: 'My Studio Project',
      currentProject: null,
      iframeUrl: null,
      currentLogo: null,
      sessionId: null,
      studioMode: '',
      previewDevice: 'desktop',
      mobileWorkspaceView: 'edit',
      isBuilding: null,
      isBuildDone: false,
      pendingCommand: null,
      pendingGenerateRequestConfig: null,
      buildVariant: 'standard',
      buildTrackingContext: null,
      cloneSourceUrl: null,
      productRequestConfig: null,
      autoPublishContext: null,
      historyItems: [],
      selectedHistoryItem: null,

      // --- UI 状态默认值 ---
      isHistoryOpen: false,
      isSettingsOpen: false,
      activeSettingsTab: 'seo',
      isHistoryModalOpen: false,
      isHistoryListModalOpen: false,
      isPublishModalOpen: false,
      isPublishSuccessModalOpen: false,
      lastPublishedUrl: null,
      currentSiteInfo: null,
      isGadsTagConnected: false,
      iframeElement: null,
      generatedHtml: '',
      previewHtmlOverride: null,
      creditBalance: null,
      creditBalanceLoading: false,
      currentDraftId: null,
      currentDraftVersion: null,
      currentDraftStatus: null,
      previewReview: {
        active: false,
        draftId: null,
        version: null,
        view: 'after',
        beforeHtml: '',
        afterHtml: '',
        title: '',
        note: '',
        sourceMessageId: null,
      },
      isPreviewApplying: false,

      /**
       * 手动切换项目基本信息
       */
      setCurrentProject: (
        id,
        currentPath = null,
        currentName = 'My Studio Project',
        siteId = null,
      ) =>
        set({
          currentId: id,
          currentProjectId: id,
          currentPath,
          currentName,
          currentSiteId: siteId,
        }),

      /**
       * 深度映射并同步项目数据到各细分字段
       */
      setCurrentProjectData: (currentProject) => {
        const normalizedProject = normalizeStudioProject(currentProject);
        const {
          id,
          sessionId,
          projectId,
          projectTypeId,
          projectName,
          projectLogo,
          projectPath,
          sitePaths,
        } = normalizedProject || {};

        // 尝试从 sitePaths 提取第一个 siteId
        const siteId = sitePaths && sitePaths.length > 0 ? String(sitePaths[0].siteId) : null;

        // 从 sitePaths 推导已发布的真实 URL（有自定义域名用自定义域名，否则用平台域名）
        const sitePath = sitePaths?.[0];
        // 去掉 path 开头的斜杠，避免拼接时出现双斜杠（如 domain//path）
        // 优先使用 localStorage 中持久化的正确 live URL（publish 时写入）
        const savedUrl = id
          ? normalizeLegacyPlatformUrl(localStorage.getItem(`pub_url_${id}`))
          : '';
        const derivedPublishedUrl = savedUrl || null;

        set((state) => {
          const matchedSiteInfo =
            state.currentSiteInfo && siteId && String(state.currentSiteInfo.id) === String(siteId)
              ? state.currentSiteInfo
              : null;
          const resolvedPublishedUrl =
            buildPublishedPageUrl({
              site: matchedSiteInfo,
              sitePath,
            }) ||
            derivedPublishedUrl ||
            null;
          // 若 API 响应未携带 projectType，从现有状态继承，防止 AI 模式标记丢失
          const mergedProject =
            normalizedProject &&
            normalizedProject.projectType === undefined &&
            state.currentProject?.projectType !== undefined
              ? { ...normalizedProject, projectType: state.currentProject.projectType }
              : normalizedProject;

          return {
            currentProject: mergedProject,
            currentId: id ? String(id) : null,
            sessionId: sessionId ? String(sessionId) : null,
            currentProjectId: projectId ? String(projectId) : id ? String(id) : null,
            currentSiteId: siteId,
            currentProjectTypeId: String(projectTypeId),
            currentName: projectName || 'My Studio Project',
            currentLogo: projectLogo || null,
            currentPath: projectPath || null,
            lastPublishedUrl: resolvedPublishedUrl,
          };
        });
      },

      /**
       * 保持 sessionId 同步，同时更新 currentProject 中的冗余备份
       */
      setSessionId: (sessionId) =>
        set((state) => ({
          sessionId,
          currentProject: state.currentProject
            ? { ...state.currentProject, sessionId: sessionId || undefined }
            : null,
        })),

      /**
       * 保持 currentSiteId 同步
       */
      setSiteId: (currentSiteId) => set({ currentSiteId }),

      /** 设置预览地址 */
      setIframeUrl: (iframeUrl) => set({ iframeUrl }),

      /** 设置 iframe DOM 元素引用 */
      setIframeElement: (iframeElement) => set({ iframeElement }),

      /** 更新实时生成的 HTML 片段 */
      setGeneratedHtml: (generatedHtml) => set({ generatedHtml, previewHtmlOverride: null }),
      setPreviewHtmlOverride: (previewHtmlOverride) => set({ previewHtmlOverride }),
      setCreditBalanceState: (creditBalance, creditBalanceLoading = false) =>
        set({ creditBalance, creditBalanceLoading }),
      setCurrentDraftMeta: (
        currentDraftId,
        currentDraftVersion = null,
        currentDraftStatus = null,
      ) => set({ currentDraftId, currentDraftVersion, currentDraftStatus }),
      startPreviewReview: (review) =>
        set({
          previewReview: {
            active: true,
            draftId: review.draftId,
            version: review.version,
            view: review.view ?? 'after',
            beforeHtml: review.beforeHtml,
            afterHtml: review.afterHtml,
            title: review.title,
            note: review.note,
            sourceMessageId: review.sourceMessageId,
          },
        }),
      setPreviewReviewView: (view) =>
        set((state) => ({
          previewReview: {
            ...state.previewReview,
            view,
          },
        })),
      clearPreviewReview: () =>
        set((state) => ({
          previewReview: {
            ...state.previewReview,
            active: false,
            draftId: null,
            version: null,
            view: 'after',
            beforeHtml: '',
            afterHtml: '',
            title: '',
            note: '',
            sourceMessageId: null,
          },
          isPreviewApplying: false,
        })),
      setPreviewApplying: (isPreviewApplying) => set({ isPreviewApplying }),

      /** 设置模式 */
      setStudioMode: (studioMode) => set({ studioMode }),
      /** 设置预览设备 */
      setPreviewDevice: (previewDevice) => set({ previewDevice }),
      setMobileWorkspaceView: (mobileWorkspaceView) => set({ mobileWorkspaceView }),
      /** 设置构建状态 */
      setIsBuilding: (isBuilding) => set({ isBuilding }),
      setIsBuildDone: (isBuildDone) => set({ isBuildDone }),
      setBuildVariant: (buildVariant, cloneSourceUrl = null, productRequestConfig = null) =>
        set({
          buildVariant,
          cloneSourceUrl: buildVariant === 'clone' ? cloneSourceUrl : null,
          productRequestConfig: buildVariant === 'product' ? productRequestConfig : null,
        }),
      setBuildTrackingContext: (buildTrackingContext) => set({ buildTrackingContext }),
      setAutoPublishContext: (autoPublishContext) => set({ autoPublishContext }),
      /** 设置待执行命令 */
      setPendingCommand: (pendingCommand) =>
        set({
          pendingCommand,
          ...(pendingCommand ? {} : { pendingGenerateRequestConfig: null }),
        }),
      setPendingGenerateRequestConfig: (pendingGenerateRequestConfig) =>
        set({ pendingGenerateRequestConfig }),
      /** 更新历史记录 */
      setHistoryItems: (historyItems) => set({ historyItems }),
      /** 追加历史项 */
      addHistoryItem: (item) =>
        set((state) => ({
          historyItems: [
            {
              id: item.id || `hist-${nanoid()}`,
              title: item.title || '变更记录',
              detail: item.detail || '',
              status: item.status || 'done',
              source: item.source || 'AI',
              at: item.at || new Date().toLocaleString(),
              ...item,
            } as HistoryItem,
            ...state.historyItems,
          ],
        })),
      /** 选择历史项 */
      setSelectedHistoryItem: (selectedHistoryItem) => set({ selectedHistoryItem }),

      /**
       /**
       * 彻底重置当前工作台的状态，清空当前项目、会话。
       * 保持预览设备 (desktop/etc) 以及 UI 状态。
       */
      clearStudioState: () =>
        set({
          currentId: null,
          currentProjectId: null,
          currentSiteId: null,
          currentPath: null,
          currentProjectTypeId: null,
          currentName: 'My Studio Project',
          currentProject: null,
          iframeUrl: null,
          currentLogo: null,
          sessionId: null,
          isBuilding: null,
          isBuildDone: false,
          pendingCommand: null,
          pendingGenerateRequestConfig: null,
          buildVariant: 'standard',
          buildTrackingContext: null,
          cloneSourceUrl: null,
          productRequestConfig: null,
          autoPublishContext: null,
          historyItems: [],
          selectedHistoryItem: null,
          studioMode: '',
          mobileWorkspaceView: 'edit',
          currentSiteInfo: null,
          isGadsTagConnected: false,
          lastPublishedUrl: null,
          generatedHtml: '',
          previewHtmlOverride: null,
          creditBalance: null,
          creditBalanceLoading: false,
          currentDraftId: null,
          currentDraftVersion: null,
          currentDraftStatus: null,
          previewReview: {
            active: false,
            draftId: null,
            version: null,
            view: 'after',
            beforeHtml: '',
            afterHtml: '',
            title: '',
            note: '',
            sourceMessageId: null,
          },
          isPreviewApplying: false,
        }),

      // --- UI Actions 实现 ---
      setCurrentSiteInfo: (site) =>
        set((state) => {
          const normalizedSite = normalizeStudioSite(site);
          const sitePath = state.currentProject?.sitePaths?.[0];
          const shouldSyncPublishedUrl =
            normalizedSite &&
            sitePath &&
            String(normalizedSite.id) === String(sitePath.siteId || '');
          const nextPublishedUrl = shouldSyncPublishedUrl
            ? buildPublishedPageUrl({
                site: normalizedSite,
                sitePath,
              }) || null
            : state.lastPublishedUrl;

          if (shouldSyncPublishedUrl && state.currentId) {
            if (nextPublishedUrl) {
              localStorage.setItem(`pub_url_${state.currentId}`, nextPublishedUrl);
            } else {
              localStorage.removeItem(`pub_url_${state.currentId}`);
            }
          }

          return {
            currentSiteInfo: normalizedSite,
            lastPublishedUrl: nextPublishedUrl,
          };
        }),
      setGadsTagConnected: (val) => set({ isGadsTagConnected: val }),
      /** 直接设置历史侧栏显隐 */
      setHistoryOpen: (val) => set({ isHistoryOpen: val }),
      /** 切换历史侧栏 */
      toggleHistory: () => set((state) => ({ isHistoryOpen: !state.isHistoryOpen })),
      /** 直接设置设置面板显隐 */
      setSettingsOpen: (val) => set({ isSettingsOpen: val }),
      /** 设置当前选中的设置页签 */
      setActiveSettingsTab: (activeSettingsTab) => set({ activeSettingsTab }),
      /** 切换设置面板 */
      toggleSettings: () => set((state) => ({ isSettingsOpen: !state.isSettingsOpen })),
      /** 设置历史弹窗 */
      setHistoryModalOpen: (val) => set({ isHistoryModalOpen: val }),
      /** 设置历史列表弹窗 */
      setHistoryListModalOpen: (val) => set({ isHistoryListModalOpen: val }),
      /** 设置发布确认弹窗 */
      setPublishModalOpen: (val) => set({ isPublishModalOpen: val }),
      /** 设置发布成功弹窗 */
      setPublishSuccessModalOpen: (val) => set({ isPublishSuccessModalOpen: val }),
      /** 设置最近一次发布的预览 URL */
      setLastPublishedUrl: (lastPublishedUrl) =>
        set({ lastPublishedUrl: normalizeLegacyPlatformUrl(lastPublishedUrl) || null }),
    }),
    {
      /** 持久化配置名称 */
      name: 'vibe-studio-storage',
      /** 使用 sessionStorage 以支持多标签页隔离，并防止数据冲突 */
      storage: createJSONStorage(() => sessionStorage),
      merge: (persistedState, currentState) => {
        const typedPersistedState = persistedState as Partial<StudioState> | undefined;
        if (!typedPersistedState) return currentState;

        return {
          ...currentState,
          ...typedPersistedState,
          currentProject: normalizeStudioProject(
            typedPersistedState.currentProject ?? currentState.currentProject,
          ),
          currentSiteInfo: normalizeStudioSite(
            typedPersistedState.currentSiteInfo ?? currentState.currentSiteInfo,
          ),
          lastPublishedUrl:
            normalizeLegacyPlatformUrl(
              typedPersistedState.lastPublishedUrl ?? currentState.lastPublishedUrl,
            ) || null,
        };
      },
      /** 选择性持久化核心字段，排除大体积历史记录和 UI 状态 */
      partialize: (state) => ({
        previewDevice: state.previewDevice,
        studioMode: state.studioMode,
        currentProject: state.currentProject,
        currentName: state.currentName,
        currentId: state.currentId,
        currentProjectId: state.currentProjectId,
        currentProjectTypeId: state.currentProjectTypeId,
        currentSiteId: state.currentSiteId,
        sessionId: state.sessionId,
        lastPublishedUrl: state.lastPublishedUrl,
        buildVariant: state.buildVariant,
        cloneSourceUrl: state.cloneSourceUrl,
        productRequestConfig: state.productRequestConfig,
        autoPublishContext: state.autoPublishContext,
        currentDraftId: state.currentDraftId,
        currentDraftVersion: state.currentDraftVersion,
        currentDraftStatus: state.currentDraftStatus,
      }),
    },
  ),
);
